// src/features/health/hooks/useMedications.ts
// React Query hooks for medication management

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/core/lib/api';
import type {
  Medication,
  MedicationFormData,
  MedicationListParams,
  MedicationListResponse,
  MedicationScheduleParams,
  DailyMedicationSchedule,
  LogMedicationDoseRequest,
  MedicationDose,
} from '../types/medicationTypes';

// ============================================================================
// Query Keys
// ============================================================================

export const medicationKeys = {
  all: ['medications'] as const,
  lists: () => [...medicationKeys.all, 'list'] as const,
  list: (params: MedicationListParams) => [...medicationKeys.lists(), params] as const,
  details: () => [...medicationKeys.all, 'detail'] as const,
  detail: (id: string) => [...medicationKeys.details(), id] as const,
  schedules: () => [...medicationKeys.all, 'schedule'] as const,
  schedule: (params: MedicationScheduleParams) => [...medicationKeys.schedules(), params] as const,
  doses: () => [...medicationKeys.all, 'doses'] as const,
  doseHistory: (medicationId: string) => [...medicationKeys.doses(), medicationId] as const,
};

// ============================================================================
// API Functions
// ============================================================================

const fetchMedications = async (params: MedicationListParams): Promise<MedicationListResponse> => {
  const { data } = await api.get(`/children/${params.childId}/medications`, {
    params: {
      is_active: params.isActive,
      search: params.search,
      page: params.page || 1,
      limit: params.limit || 20,
    },
  });
  return data;
};

const fetchMedication = async (medicationId: string): Promise<Medication> => {
  const { data } = await api.get(`/medications/${medicationId}`);
  return data;
};

const createMedication = async ({
  childId,
  data,
}: {
  childId: string;
  data: MedicationFormData;
}): Promise<Medication> => {
  const response = await api.post(`/children/${childId}/medications`, data);
  return response.data;
};

const updateMedication = async ({
  medicationId,
  data,
}: {
  medicationId: string;
  data: Partial<MedicationFormData>;
}): Promise<Medication> => {
  const response = await api.patch(`/medications/${medicationId}`, data);
  return response.data;
};

const deleteMedication = async (medicationId: string): Promise<void> => {
  await api.delete(`/medications/${medicationId}`);
};

const fetchMedicationSchedule = async (
  params: MedicationScheduleParams
): Promise<DailyMedicationSchedule> => {
  const { data } = await api.get(`/children/${params.childId}/medications/schedule`, {
    params: { date: params.date },
  });
  return data;
};

const logMedicationDose = async (
  childId: string,
  request: LogMedicationDoseRequest
): Promise<MedicationDose> => {
  const { data } = await api.post(`/children/${childId}/medications/doses`, request);
  return data;
};

const fetchDoseHistory = async (
  medicationId: string,
  params?: { startDate?: string; endDate?: string; limit?: number }
): Promise<MedicationDose[]> => {
  const { data } = await api.get(`/medications/${medicationId}/doses`, { params });
  return data;
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch paginated list of medications for a child
 */
export function useMedications(params: MedicationListParams) {
  return useQuery({
    queryKey: medicationKeys.list(params),
    queryFn: () => fetchMedications(params),
    enabled: !!params.childId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Fetch active medications only
 */
export function useActiveMedications(childId: string) {
  return useMedications({ childId, isActive: true });
}

/**
 * Fetch a single medication by ID
 */
export function useMedication(medicationId: string | undefined) {
  return useQuery({
    queryKey: medicationKeys.detail(medicationId!),
    queryFn: () => fetchMedication(medicationId!),
    enabled: !!medicationId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Fetch medication schedule for a specific date
 */
export function useMedicationSchedule(params: MedicationScheduleParams) {
  return useQuery({
    queryKey: medicationKeys.schedule(params),
    queryFn: () => fetchMedicationSchedule(params),
    enabled: !!params.childId && !!params.date,
    staleTime: 1 * 60 * 1000, // 1 minute - schedule data should be fresh
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });
}

/**
 * Fetch dose history for a medication
 */
export function useDoseHistory(
  medicationId: string | undefined,
  params?: { startDate?: string; endDate?: string; limit?: number }
) {
  return useQuery({
    queryKey: [...medicationKeys.doseHistory(medicationId!), params],
    queryFn: () => fetchDoseHistory(medicationId!, params),
    enabled: !!medicationId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Create a new medication
 */
export function useCreateMedication() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createMedication,
    onSuccess: (_, variables) => {
      // Invalidate medication lists for this child
      queryClient.invalidateQueries({
        queryKey: medicationKeys.lists(),
        predicate: (query) => {
          const params = query.queryKey[2] as MedicationListParams | undefined;
          return params?.childId === variables.childId;
        },
      });
      // Invalidate schedules
      queryClient.invalidateQueries({
        queryKey: medicationKeys.schedules(),
      });
    },
  });
}

/**
 * Update an existing medication
 */
export function useUpdateMedication() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateMedication,
    onSuccess: (data) => {
      // Update the specific medication in cache
      queryClient.setQueryData(medicationKeys.detail(data.id), data);
      // Invalidate lists
      queryClient.invalidateQueries({
        queryKey: medicationKeys.lists(),
      });
      // Invalidate schedules
      queryClient.invalidateQueries({
        queryKey: medicationKeys.schedules(),
      });
    },
  });
}

/**
 * Delete a medication
 */
export function useDeleteMedication() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteMedication,
    onSuccess: (_, medicationId) => {
      // Remove from cache
      queryClient.removeQueries({
        queryKey: medicationKeys.detail(medicationId),
      });
      // Invalidate lists
      queryClient.invalidateQueries({
        queryKey: medicationKeys.lists(),
      });
      // Invalidate schedules
      queryClient.invalidateQueries({
        queryKey: medicationKeys.schedules(),
      });
    },
  });
}

/**
 * Log a medication dose (taken, missed, skipped)
 */
export function useLogMedicationDose(childId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: LogMedicationDoseRequest) => logMedicationDose(childId, request),
    onSuccess: (_, variables) => {
      // Invalidate schedules
      queryClient.invalidateQueries({
        queryKey: medicationKeys.schedules(),
      });
      // Invalidate dose history for this medication
      queryClient.invalidateQueries({
        queryKey: medicationKeys.doseHistory(variables.medicationId),
      });
    },
  });
}

/**
 * Toggle medication active status
 */
export function useToggleMedicationStatus() {
  const updateMedication = useUpdateMedication();

  return {
    ...updateMedication,
    mutate: (medicationId: string, isActive: boolean) => {
      updateMedication.mutate({ medicationId, data: { isActive } });
    },
    mutateAsync: async (medicationId: string, isActive: boolean) => {
      return updateMedication.mutateAsync({ medicationId, data: { isActive } });
    },
  };
}

// ============================================================================
// Prefetch Functions
// ============================================================================

export function usePrefetchMedications() {
  const queryClient = useQueryClient();

  return async (params: MedicationListParams) => {
    await queryClient.prefetchQuery({
      queryKey: medicationKeys.list(params),
      queryFn: () => fetchMedications(params),
      staleTime: 2 * 60 * 1000,
    });
  };
}

export function usePrefetchMedication() {
  const queryClient = useQueryClient();

  return async (medicationId: string) => {
    await queryClient.prefetchQuery({
      queryKey: medicationKeys.detail(medicationId),
      queryFn: () => fetchMedication(medicationId),
      staleTime: 5 * 60 * 1000,
    });
  };
}

// ============================================================================
// Utility Hooks
// ============================================================================

/**
 * Get today's medication schedule with auto-refresh
 */
export function useTodaysMedicationSchedule(childId: string) {
  const today = new Date().toISOString().split('T')[0];
  return useMedicationSchedule({ childId, date: today });
}

/**
 * Calculate medication adherence from dose history
 */
export function useMedicationAdherenceCalculation(
  medicationId: string,
  startDate: string,
  endDate: string
) {
  const { data: doses, ...rest } = useDoseHistory(medicationId, { startDate, endDate });

  const adherenceStats = doses
    ? {
        total: doses.length,
        taken: doses.filter((d) => d.status === 'taken').length,
        missed: doses.filter((d) => d.status === 'missed').length,
        skipped: doses.filter((d) => d.status === 'skipped').length,
        late: doses.filter((d) => d.status === 'late').length,
        adherenceRate:
          doses.length > 0
            ? (doses.filter((d) => d.status === 'taken' || d.status === 'late').length /
                doses.length) *
              100
            : 0,
      }
    : null;

  return { ...rest, data: adherenceStats };
}

